import { useState, useEffect } from 'react';
import { useSpring, easings } from '@react-spring/three';
import { useExperienceStore } from '../../store';
import { styled, keyframes } from '@/stitches.config';
import { motion, AnimatePresence } from 'framer-motion';

const SIZE = 60;

export function Mouse() {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [progress, setProgress] = useState(0);

  const {
    updateActiveIndex,
    hoverIndex,
    updateHoverIndex,
    exploreState,
    activeIndexes,
  } = useExperienceStore((state) => ({
    updateActiveIndex: state.updateActiveIndex,
    hoverIndex: state.hoverIndex,
    updateHoverIndex: state.updateHoverIndex,
    exploreState: state.exploreState,
    activeIndexes: state.activeIndexes,
  }));

  const hoverIndexIsDefind = typeof hoverIndex === 'number';

  useSpring({
    transform: hoverIndexIsDefind ? 1 : 0,
    config: {
      duration: hoverIndexIsDefind ? 800 : 150,
      easing: easings.linear,
    },
    onChange: ({ value }) => {
      setProgress(value.transform);

      if (value.transform === 1) {
        updateActiveIndex(hoverIndex as number);
        updateHoverIndex(null);
      }
    },
  });

  useEffect(() => {
    const setFromEvent = (event: MouseEvent) => {
      setMousePosition({ x: event.clientX, y: event.clientY });
    };

    window.addEventListener('mousemove', setFromEvent);

    return () => {
      window.removeEventListener('mousemove', setFromEvent);
    };
  }, []);

  const radius = SIZE / 2;
  const strokeWidth = 6;

  const normalizedRadius = radius - strokeWidth * 2;
  const circumference = normalizedRadius * 2 * Math.PI;

  const strokeDashoffset = circumference - (0 / 100) * circumference;

  return (
    <div
      style={{
        position: 'absolute',
        top: mousePosition.y - SIZE / 2,
        left: mousePosition.x - SIZE / 2,
        height: SIZE,
        width: SIZE,
        pointerEvents: 'none',
      }}
    >
      <AnimatePresence>
        {exploreState === 'cluster' && activeIndexes.length === 0 && (
          <OrbContainer
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0 }}
            key="cluster-when-nothing-is-active"
          >
            <FadeOutContainer>
              <svg
                height={radius * 2}
                width={radius * 2}
                style={{
                  overflow: 'auto',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                }}
              >
                <Circle
                  strokeWidth={strokeWidth}
                  fill="transparent"
                  css={{
                    stroke: '$foreground',
                  }}
                  r={normalizedRadius}
                  cx={radius}
                  cy={radius}
                />
              </svg>
            </FadeOutContainer>
          </OrbContainer>
        )}
        {exploreState === 'cluster' && (
          <svg
            height={radius * 2}
            width={radius * 2}
            style={{
              overflow: ' auto',
              position: 'absolute',
              top: 0,
              left: 0,
            }}
          >
            <Circle
              fill="transparent"
              strokeWidth={strokeWidth}
              strokeDasharray={circumference + ' ' + circumference}
              css={{
                strokeDashoffset: (progress - 1) * strokeDashoffset,
                transform: 'rotate(-90deg) scaleY(-1)',
                opacity: progress * 2,
                transformOrigin: 'center',
                stroke: '$foreground',
                willChange: 'stroke-dashoffset',
              }}
              r={normalizedRadius}
              cx={radius}
              cy={radius}
            />
          </svg>
        )}
      </AnimatePresence>
    </div>
  );
}

const Circle = styled('circle');

const rotate = keyframes({
  '0%': { transform: 'rotate(0deg)' },
  '100%': { transform: 'rotate(360deg)' },
});

const scaleDown = keyframes({
  '0%': { transform: 'scale(1)' },
  '100%': { transform: 'scale(0)' },
});

const FadeOutContainer = styled(motion.div, {
  animation: `${scaleDown} 0.5s forwards`,
  animationDelay: '0.15s',
});

const OrbContainer = styled(motion.div, {
  height: '100%',
  width: '100%',
  position: 'absolute',
  top: 0,
  left: 0,
  animation: `${rotate} 15s infinite linear`,
  transformOrigin: 'center',

  svg: {
    fill: '$foreground',
  },
});
